import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Send, ShieldAlert, FileDown, Printer, PanelLeftOpen, PanelLeftClose, Plus, Trash2, MessageSquare } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { apiPost, apiDelete } from '../lib/auth'
import { marked } from 'marked'
import * as echarts from 'echarts'
import ContextForm from '../components/ai/ContextForm'
import '../styles/report-template.css'
import reportCssText from '../styles/report-template.css?raw'

interface Message {
  role: 'user' | 'assistant'
  content: string
  tokensIn?: number
  tokensOut?: number
  cost?: number
  toolCalls?: Array<{ name: string }>
  createdAt?: string
}

interface ConversationSummary {
  id: number
  title: string
  messageCount?: number
  totalCost?: number
  updatedAt: string
}

interface ConversationDetail {
  id: number
  clientId: number
  title: string
  contextText?: string
  messages: Message[]
}

function relativeDate(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "A l'instant"
  if (mins < 60) return `${mins}min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}j`
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
}

// Strip HTML tags for chat preview
function stripHtml(html: string): string {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

// Check if content looks like HTML (has tags) vs plain markdown
function isHtmlContent(content: string): boolean {
  return /<(div|h[1-6]|p|table|ul|ol|span|section)\b/i.test(content)
}

// Clean Claude output: strip code fences, trim whitespace
function cleanHtmlContent(content: string): string {
  let cleaned = content.trim()
  // Remove ```html ... ``` wrapper if present
  cleaned = cleaned.replace(/^```html?\s*\n?/i, '').replace(/\n?```\s*$/g, '')
  // Remove any remaining ``` blocks wrapping HTML
  cleaned = cleaned.replace(/```html?\s*\n([\s\S]*?)\n```/gi, '$1')
  return cleaned.trim()
}

// Initialize ECharts instances on .chart divs inside a container
function initCharts(container: HTMLElement) {
  const chartDivs = container.querySelectorAll<HTMLElement>('.chart[data-config]')
  chartDivs.forEach((div) => {
    // Skip if already initialized
    if (echarts.getInstanceByDom(div)) return

    const type = div.dataset.type || 'bar'
    let config: any
    try {
      config = JSON.parse(div.dataset.config || '{}')
    } catch {
      return
    }

    const chart = echarts.init(div)
    const option = buildEChartsOption(type, config)
    if (option) chart.setOption(option)

    // Resize observer
    const ro = new ResizeObserver(() => chart.resize())
    ro.observe(div)
  })
}

// Build ECharts option from simplified config
function buildEChartsOption(type: string, config: any): echarts.EChartsOption | null {
  const baseColors = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6']

  switch (type) {
    case 'line':
    case 'bar':
      return {
        tooltip: { trigger: 'axis' },
        legend: config.series?.length > 1
          ? { data: config.series.map((s: any) => s.name), top: 0, left: 'center', itemGap: 20, textStyle: { fontSize: 12 } }
          : undefined,
        grid: { left: '3%', right: '4%', top: config.series?.length > 1 ? 35 : 15, bottom: 10, containLabel: true },
        xAxis: { type: 'category', data: config.xAxis || [], axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { fontSize: 11 } },
        series: (config.series || []).map((s: any, i: number) => ({
          name: s.name,
          type,
          data: s.data,
          smooth: type === 'line',
          itemStyle: { color: baseColors[i % baseColors.length] },
          areaStyle: type === 'line' ? { opacity: 0.08 } : undefined,
        })),
      }

    case 'pie':
      return {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          data: config.series?.[0]?.data || [],
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
          label: { show: true, formatter: '{b}\n{d}%' },
        }],
        color: baseColors,
      }

    case 'gauge':
      return {
        series: [{
          type: 'gauge',
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: 100,
          progress: { show: true, width: 14 },
          axisLine: { lineStyle: { width: 14, color: [[0.3, '#ef4444'], [0.7, '#f59e0b'], [1, '#22c55e']] } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          pointer: { show: false },
          title: { offsetCenter: [0, '70%'], fontSize: 14, color: '#374151' },
          detail: { valueAnimation: true, fontSize: 32, fontWeight: 700, offsetCenter: [0, '30%'], color: '#111827' },
          data: [{ value: config.value || 0, name: config.name || '' }],
        }],
      }

    default:
      return null
  }
}

// Build exportable HTML from the rendered DOM — charts become static images
function buildExportFromDom(title: string, docBody: HTMLElement, totalCost: number): string {
  const date = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })

  // Clone the rendered document to avoid modifying the live DOM
  const clone = docBody.cloneNode(true) as HTMLElement

  // Replace ECharts divs with static PNG images
  const chartDivs = clone.querySelectorAll<HTMLElement>('.chart[data-config]')
  chartDivs.forEach((div) => {
    // Find the original div in the live DOM to get the ECharts instance
    const origDiv = docBody.querySelector<HTMLElement>(
      `.chart[data-type="${div.dataset.type}"][data-config='${div.dataset.config}']`
    )
    if (origDiv) {
      const instance = echarts.getInstanceByDom(origDiv)
      if (instance) {
        const dataUrl = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
        const img = document.createElement('img')
        img.src = dataUrl
        img.alt = 'Graphique'
        img.style.cssText = 'max-width:100%;margin:16px 0;border-radius:10px'
        div.replaceWith(img)
        return
      }
    }
    // If no instance found, just remove the empty chart div
    div.remove()
  })

  const bodyHtml = clone.innerHTML

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"/><title>${title}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',sans-serif;color:#111827;line-height:1.5;max-width:900px;margin:0 auto;padding:40px 50px}
.header{border-bottom:3px solid #6366F1;padding-bottom:16px;margin-bottom:30px}
.header h1{font-size:22px;font-weight:700}
.header .meta{font-size:12px;color:#6b7280;margin-top:4px}
.header .badge{display:inline-block;background:#6366F1;color:white;font-size:10px;font-weight:600;padding:2px 10px;border-radius:12px;margin-left:8px}
.footer{margin-top:40px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#6b7280;text-align:center}
img{page-break-inside:avoid}
@media print{body{padding:20px 30px}}
/* Report template classes (unscoped for export) */
${reportCssText.replace(/\.report-document\s*/g, '')}
</style></head><body>
<div class="header">
<h1>${title} <span class="badge">AI Analyst</span></h1>
<div class="meta">${date} — Rapport genere par Orion SEO${totalCost > 0 ? ` — Cout: ${totalCost.toFixed(3)}€` : ''}</div>
</div>
${bodyHtml}
<div class="footer">Rapport genere par Orion SEO — orionseo.fr</div>
</body></html>`
}

export default function AIAnalyst() {
  const { projectId } = useParams<{ projectId: string }>()
  const clientId = Number(projectId)

  const [activeConvId, setActiveConvId] = useState<number | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [refreshTick, setRefreshTick] = useState(0)

  const chatEndRef = useRef<HTMLDivElement>(null)
  const docEndRef = useRef<HTMLDivElement>(null)
  const docBodyRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Conversations list
  const { data: conversations, refetch: refetchConvList } = useApi<ConversationSummary[]>(
    `/api/ai/conversations?clientId=${clientId}`
  )

  // Load conversation when selected
  const { data: convDetail } = useApi<ConversationDetail>(
    activeConvId ? `/api/ai/conversations/${activeConvId}` : ''
  )

  useEffect(() => {
    if (convDetail?.messages) {
      // Filter to user/assistant only, ensure content is always a string
      const cleaned = convDetail.messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({
          ...m,
          content: typeof m.content === 'string' ? m.content : (m.content ? JSON.stringify(m.content) : ''),
          toolCalls: Array.isArray(m.toolCalls) ? m.toolCalls : undefined,
        }))
      setMessages(cleaned)
    }
  }, [convDetail])

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    docEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Initialize ECharts after messages render
  useEffect(() => {
    if (docBodyRef.current) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        if (docBodyRef.current) initCharts(docBodyRef.current)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [messages])

  const refetchConversations = useCallback(() => {
    setRefreshTick(t => t + 1)
    refetchConvList()
  }, [refetchConvList])

  // SSE message sending
  const sendMessage = useCallback(
    async (content: string, conversationId: number) => {
      setLoading(true)
      setMessages(prev => [...prev, { role: 'user', content, createdAt: new Date().toISOString() }])

      try {
        const res = await fetch(`/api/ai/conversations/${conversationId}/messages`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content }),
        })

        if (res.status === 403) {
          setForbidden(true)
          setLoading(false)
          return
        }

        const reader = res.body?.getReader()
        const decoder = new TextDecoder()

        while (reader) {
          const { done, value } = await reader.read()
          if (done) break

          const text = decoder.decode(value)
          const lines = text.split('\n').filter(l => l.startsWith('data: '))

          for (const line of lines) {
            const data = line.slice(6)
            if (data === '[DONE]') break
            try {
              const parsed = JSON.parse(data)
              if (parsed.type === 'message') {
                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: typeof parsed.content === 'string' ? parsed.content : JSON.stringify(parsed.content ?? ''),
                  tokensIn: parsed.tokensIn,
                  tokensOut: parsed.tokensOut,
                  cost: parsed.cost,
                  toolCalls: Array.isArray(parsed.toolCalls) ? parsed.toolCalls : undefined,
                  createdAt: new Date().toISOString(),
                }])
              }
            } catch {}
          }
        }
      } catch (err) {
        console.error('SSE error:', err)
      }

      setLoading(false)
      refetchConversations()
    },
    [refetchConversations]
  )

  const handleNewConversation = async (data: {
    contextText: string
    contextFields: Record<string, string>
    firstMessage: string
  }) => {
    setLoading(true)
    const { data: conv, error } = await apiPost<{ id: number }>('/api/ai/conversations', {
      clientId,
      contextText: data.contextText || undefined,
      contextFields: Object.keys(data.contextFields).length > 0 ? data.contextFields : undefined,
    })

    if (error) {
      if (error.includes('403') || error.includes('permission')) setForbidden(true)
      setLoading(false)
      return
    }
    if (!conv?.id) { setLoading(false); return }

    setActiveConvId(conv.id)
    setMessages([])
    refetchConversations()
    await sendMessage(data.firstMessage, conv.id)
  }

  const handleSend = async () => {
    if (!input.trim() || !activeConvId || loading) return
    const content = input.trim()
    setInput('')
    await sendMessage(content, activeConvId)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const handleExport = (andPrint = false) => {
    if (!docBodyRef.current) return
    const title = convDetail?.title || 'Analyse AI'
    const html = buildExportFromDom(title, docBodyRef.current, totalCost)
    const win = window.open('', '_blank')
    if (win) {
      win.document.write(html)
      win.document.close()
      if (andPrint) {
        win.onload = () => win.print()
      }
    }
  }

  const handleDeleteConv = async (id: number) => {
    if (!confirm('Supprimer cette analyse ?')) return
    await apiDelete(`/api/ai/conversations/${id}`)
    if (activeConvId === id) { setActiveConvId(null); setMessages([]) }
    refetchConversations()
  }

  // Assistant messages for document preview
  const assistantMessages = messages.filter(m => m.role === 'assistant' && m.content)
  const totalCost = messages.reduce((s, m) => s + (m.cost || 0), 0)

  if (forbidden) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ backgroundColor: 'rgba(99,102,241,0.12)' }}>
            <ShieldAlert size={28} className="text-[#6366F1]" strokeWidth={1.25} />
          </div>
          <h2 className="text-lg font-semibold text-[#1B2559] mb-2">Fonctionnalite non activee</h2>
          <p className="text-sm text-[#8B8FA7]">Contactez l'administrateur pour activer AI Analyst.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-64px)] bg-[#F8F9FC]">

      {/* -- Sidebar (collapsed by default) -- */}
      <div className={`shrink-0 border-r border-[#E2E4ED] bg-white transition-all duration-200 flex flex-col ${sidebarOpen ? 'w-56' : 'w-12'}`}>
        {/* Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-3 text-[#8B8FA7] hover:text-[#6366F1] transition-colors border-b border-[#E2E4ED]"
          title={sidebarOpen ? 'Reduire' : 'Etendre'}
        >
          {sidebarOpen ? <PanelLeftClose size={18} strokeWidth={1.25} /> : <PanelLeftOpen size={18} strokeWidth={1.25} />}
        </button>

        {/* New button */}
        <button
          onClick={() => { setActiveConvId(null); setMessages([]) }}
          className={`p-3 text-[#8B8FA7] hover:text-[#6366F1] transition-colors ${sidebarOpen ? 'flex items-center gap-2 px-3' : ''}`}
          title="Nouvelle analyse"
        >
          <Plus size={18} strokeWidth={1.25} />
          {sidebarOpen && <span className="text-xs font-medium">Nouvelle analyse</span>}
        </button>

        {/* Conversations list (only when open) */}
        {sidebarOpen && (
          <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
            {(conversations || []).map(conv => (
              <div
                key={conv.id}
                onClick={() => { setActiveConvId(conv.id); setMessages([]) }}
                className={`group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer text-xs transition-colors ${
                  activeConvId === conv.id
                    ? 'bg-[#6366F1]/10 text-[#6366F1]'
                    : 'text-[#3B3F5C] hover:bg-[#F4F5FA]'
                }`}
              >
                <MessageSquare size={13} strokeWidth={1.25} className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{conv.title}</div>
                  <div className="text-[10px] text-[#8B8FA7]">{relativeDate(conv.updatedAt)}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteConv(conv.id) }}
                  className="opacity-0 group-hover:opacity-100 text-[#8B8FA7] hover:text-[#EF4444] transition-all"
                >
                  <Trash2 size={12} strokeWidth={1.25} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Collapsed: just icons for conversations */}
        {!sidebarOpen && (
          <div className="flex-1 overflow-y-auto py-1">
            {(conversations || []).slice(0, 10).map(conv => (
              <button
                key={conv.id}
                onClick={() => { setActiveConvId(conv.id); setMessages([]) }}
                className={`w-full p-3 transition-colors ${
                  activeConvId === conv.id ? 'text-[#6366F1]' : 'text-[#8B8FA7] hover:text-[#6366F1]'
                }`}
                title={conv.title}
              >
                <MessageSquare size={16} strokeWidth={1.25} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* -- Main area -- */}
      {activeConvId === null ? (
        /* New conversation */
        <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
          <ContextForm onSubmit={handleNewConversation} loading={loading} />
        </div>
      ) : (
        <div className="flex-1 flex min-w-0">

          {/* -- Chat panel (35%) -- */}
          <div className="w-[35%] shrink-0 flex flex-col border-r border-[#E2E4ED] bg-[#FAFBFD]">
            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, i) => (
                <div key={i}>
                  {msg.role === 'user' ? (
                    <div className="flex justify-end">
                      <div className="max-w-[90%] rounded-lg px-3 py-2 text-[12px] leading-relaxed bg-[#6366F1]/10 text-[#1B2559]">
                        {msg.content}
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-start">
                      <div className="max-w-[95%] rounded-lg px-3 py-2 text-[11px] leading-relaxed bg-white border border-[#E2E4ED] text-[#3B3F5C]">
                        <div className="line-clamp-4">
                          {stripHtml(msg.content).substring(0, 300)}{msg.content.length > 300 ? '...' : ''}
                        </div>
                        {msg.toolCalls && msg.toolCalls.length > 0 && (
                          <div className="mt-1 text-[10px] text-[#8B8FA7]">
                            {msg.toolCalls.length} source{msg.toolCalls.length > 1 ? 's' : ''} interrogee{msg.toolCalls.length > 1 ? 's' : ''}
                          </div>
                        )}
                        {msg.cost != null && msg.cost > 0 && (
                          <div className="mt-0.5 text-[10px] text-[#8B8FA7]">{msg.cost.toFixed(4)}€</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-lg px-3 py-2 bg-white border border-[#E2E4ED]">
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] animate-bounce [animation-delay:0ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] animate-bounce [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-[#E2E4ED] bg-white p-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  placeholder={loading ? "Analyse en cours..." : "Posez votre question..."}
                  rows={1}
                  className="flex-1 rounded-lg border border-[#E2E4ED] px-3 py-2 text-[12px] text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1] resize-none disabled:bg-[#F4F5FA] disabled:cursor-not-allowed"
                  style={{ minHeight: '36px', maxHeight: '80px' }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="shrink-0 p-2 rounded-lg text-white bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 transition-colors"
                >
                  <Send size={14} strokeWidth={1.25} />
                </button>
              </div>
            </div>
          </div>

          {/* -- Document preview panel (65%) -- */}
          <div className="flex-1 flex flex-col bg-white min-w-0">
            {/* Document header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-[#E2E4ED]">
              <div>
                <h2 className="text-sm font-semibold text-[#1B2559]">{convDetail?.title || 'Document'}</h2>
                <p className="text-[11px] text-[#8B8FA7]">
                  {assistantMessages.length} section{assistantMessages.length > 1 ? 's' : ''}
                  {totalCost > 0 && ` — ${totalCost.toFixed(4)}€`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleExport(false)}
                  disabled={assistantMessages.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E4ED] text-[12px] font-medium text-[#3B3F5C] hover:border-[#6366F1] hover:text-[#6366F1] transition-colors disabled:opacity-40"
                >
                  <FileDown size={14} strokeWidth={1.25} />
                  Exporter
                </button>
                <button
                  onClick={() => handleExport(true)}
                  disabled={assistantMessages.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E4ED] text-[12px] font-medium text-[#3B3F5C] hover:border-[#6366F1] hover:text-[#6366F1] transition-colors disabled:opacity-40"
                >
                  <Printer size={14} strokeWidth={1.25} />
                  Imprimer
                </button>
              </div>
            </div>

            {/* Document body — shows only the LATEST assistant message as the live document */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {assistantMessages.length === 0 && !loading ? (
                <div className="flex items-center justify-center h-full text-[#8B8FA7] text-sm">
                  Le document apparaitra ici au fil de l'analyse
                </div>
              ) : (
                <div ref={docBodyRef} className="report-document max-w-[750px] mx-auto">
                  {(() => {
                    // Only render the last assistant message as the document
                    const lastAssistant = assistantMessages[assistantMessages.length - 1]
                    if (!lastAssistant) return null
                    const cleaned = cleanHtmlContent(lastAssistant.content)
                    const html = isHtmlContent(cleaned)
                      ? cleaned
                      : marked.parse(cleaned, { gfm: true, breaks: true }) as string
                    return (
                      <div dangerouslySetInnerHTML={{ __html: html }} />
                    )
                  })()}

                  {loading && (
                    <div className="flex items-center gap-2 text-[#8B8FA7] text-[13px] py-4">
                      <span className="w-2 h-2 rounded-full bg-[#6366F1] animate-bounce [animation-delay:0ms]" />
                      <span className="w-2 h-2 rounded-full bg-[#6366F1] animate-bounce [animation-delay:150ms]" />
                      <span className="w-2 h-2 rounded-full bg-[#6366F1] animate-bounce [animation-delay:300ms]" />
                      <span className="ml-2">Analyse en cours...</span>
                    </div>
                  )}
                  <div ref={docEndRef} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
